import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeartPulse, UserCheck, TrendingUp, ArrowRight, Clock, CheckCircle2, Download, FileText, Calculator, FileCheck2, Gauge } from "lucide-react";
import Seo, { SITE_NAME } from "../components/Seo";
import { publicApi, mediaUrl } from "../lib/api";
import GuideDownloadModal from "../components/GuideDownloadModal";

const ASSESSMENTS = [
  {
    key: "business-health-score",
    icon: HeartPulse,
    title: "Your Business Health Score",
    question: "Is your business financially healthy?",
    description: "Assess the financial foundations of your business — cash flow, profitability, reporting, financial control and forward planning — and see whether it's secure, under pressure or carrying risks that need attention.",
    time: "About 5 minutes",
    cta: "Check Your Business Health",
    to: "/more-than-numbers/business-health-score",
    status: "live",
    accent: "teal",
  },
  {
    key: "owner-independence-score",
    icon: UserCheck,
    title: "Your Owner Independence Score",
    question: "Could your business thrive without you?",
    description: "See how heavily the business depends on you for decisions, sales, customer relationships and day-to-day operations — and whether you've built a resilient business or a demanding job.",
    time: "About 7 minutes",
    cta: "Calculate Your Independence Score",
    to: "/more-than-numbers/owner-independence-score",
    status: "live",
    accent: "orange",
  },
  {
    key: "growth-readiness-score",
    icon: TrendingUp,
    title: "Your Growth Readiness Score",
    question: "Are you truly ready to scale?",
    description: "Test whether your finances, systems, team, leadership and capacity are ready for sustainable growth — and challenge the assumption that more sales automatically means you're ready to scale.",
    time: "About 7 minutes",
    cta: "Check Your Growth Readiness",
    to: "/more-than-numbers/growth-readiness-score",
    status: "live",
    accent: "sky",
  },
  {
    key: "financial-awareness-score",
    icon: Gauge,
    title: "Your Financial Awareness Score",
    question: "Do you truly understand your numbers?",
    description: "Measure how well you understand, monitor and use the ten financial numbers every business owner should know — from cash and margins to debtor days and customer concentration — and get a traffic-light breakdown of each.",
    time: "Under 3 minutes",
    cta: "Score Your Financial Awareness",
    to: "/more-than-numbers/financial-awareness-score",
    status: "live",
    accent: "amber",
  },
];

const ACCENT = {
  teal: "bg-teal/10 text-teal",
  orange: "bg-orange/10 text-orange",
  sky: "bg-sky-100 text-sky-600",
  amber: "bg-amber-100 text-amber-600",
};

export default function BusinessAssessmentTools() {
  const [images, setImages] = useState({});
  const [guides, setGuides] = useState([]);
  const [activeGuide, setActiveGuide] = useState(null);
  const [calcTile, setCalcTile] = useState({ image: "", alt: "" });
  const [mtdTile, setMtdTile] = useState({ image: "", alt: "" });

  useEffect(() => {
    publicApi.assessmentsList().then((list) => {
      const m = {};
      (list || []).forEach((a) => { if (a.hero_image) m[a.slug] = { image: a.hero_image, alt: a.hero_image_alt }; });
      setImages(m);
    }).catch(() => {});
    publicApi.guides().then((g) => setGuides(g || [])).catch(() => {});
    publicApi.calculatorConfig().then((c) => setCalcTile({ image: c?.hub_tile_image || "", alt: c?.hub_tile_image_alt || "" })).catch(() => {});
    publicApi.mtdConfig().then((c) => setMtdTile({ image: c?.hub_tile_image || "", alt: c?.hub_tile_image_alt || "" })).catch(() => {});
  }, []);

  return (
    <div data-testid="assessment-tools-page">
      <Seo
        title="Free Business Assessment Tools"
        description="Use CASS's free business assessment tools to measure your financial health, owner independence and readiness for sustainable growth."
      />

      {/* Hero */}
      <section className="bg-navy-deep text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6">Giving you more than numbers</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">Business Assessment Tools</h1>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            Running a successful business takes more than knowing what happened last year. These practical assessments help you understand how financially healthy your business is, how dependent it remains on you, and whether it's genuinely ready for its next stage of growth.
          </p>
          <p className="text-white/90 font-semibold mt-5 max-w-2xl">Understand where your business stands today — and what needs to change next.</p>
        </div>
      </section>

      {/* Cards */}
      <section className="py-14 md:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-slate-500 max-w-2xl mb-10">Each assessment provides a personalised score, identifies the areas that need attention, and gives you clear actions to help move the business forward.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ASSESSMENTS.map((a) => {
              const Icon = a.icon;
              const coming = a.status === "coming";
              const img = images[a.key];
              return (
                <div key={a.key} data-testid={`assessment-card-${a.key}`} className="bg-white rounded-3xl border border-navy/5 shadow-sm hover:shadow-xl transition-shadow p-7 flex flex-col">
                  {img ? (
                    <img loading="lazy" decoding="async" src={mediaUrl(img.image, 700)} alt={img.alt || a.title} className="w-full h-auto rounded-2xl border border-navy/5 mb-5" data-testid={`assessment-card-image-${a.key}`} />
                  ) : (
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${ACCENT[a.accent]}`}><Icon size={26} /></div>
                  )}
                  <h2 className="font-display font-extrabold text-xl text-navy leading-snug">{a.title}</h2>
                  <p className="text-sm font-semibold text-slate-400 mt-1 mb-3">{a.question}</p>
                  <p className="text-sm text-slate-500 leading-relaxed flex-1">{a.description}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mt-5 mb-4"><Clock size={14} /> {a.time}</div>
                  {coming ? (
                    <span data-testid={`assessment-status-${a.key}`} className="inline-flex items-center justify-center gap-2 w-full bg-slate-100 text-slate-400 font-bold text-sm px-5 py-3.5 rounded-full cursor-default">Coming soon</span>
                  ) : (
                    <Link to={a.to} data-testid={`assessment-cta-${a.key}`} className="inline-flex items-center justify-center gap-2 w-full min-h-[3.25rem] text-center leading-tight bg-orange hover:bg-orange-dark text-white font-bold text-sm px-4 py-3 rounded-full transition-all hover:-translate-y-0.5">
                      {a.cta} <ArrowRight size={16} />
                    </Link>
                  )}
                </div>
              );
            })}

            {/* Key Business Numbers Calculator — first-class tool tile */}
            <div data-testid="assessment-card-numbers-calculator" className="bg-white rounded-3xl border border-navy/5 shadow-sm hover:shadow-xl transition-shadow p-7 flex flex-col">
              {calcTile.image ? (
                <img loading="lazy" decoding="async" src={mediaUrl(calcTile.image, 700)} alt={calcTile.alt || "Key Business Numbers Calculator"} className="w-full h-auto rounded-2xl border border-navy/5 mb-5" data-testid="assessment-card-image-numbers-calculator" />
              ) : (
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-navy text-orange"><Calculator size={26} /></div>
              )}
              <h2 className="font-display font-extrabold text-xl text-navy leading-snug">The Key Business Numbers Calculator</h2>
              <p className="text-sm font-semibold text-slate-400 mt-1 mb-3">What are your numbers really telling you?</p>
              <p className="text-sm text-slate-500 leading-relaxed flex-1">Calculate your key numbers — gross margin, break-even, cash runway, debtor days and more — with plain-English explanations, what-if modelling and a personalised 90-day action plan.</p>
              <div className="flex items-center gap-2 text-xs text-slate-400 mt-5 mb-4"><Clock size={14} /> A few minutes · instant results</div>
              <Link to="/business-numbers-calculator" data-testid="assessment-cta-numbers-calculator" className="inline-flex items-center justify-center gap-2 w-full min-h-[3.25rem] text-center leading-tight bg-orange hover:bg-orange-dark text-white font-bold text-sm px-4 py-3 rounded-full transition-all hover:-translate-y-0.5">
                Calculate My Numbers <ArrowRight size={16} />
              </Link>
            </div>

            {/* MTD for Income Tax checker tile */}
            <div data-testid="assessment-card-mtd" className="bg-white rounded-3xl border border-navy/5 shadow-sm hover:shadow-xl transition-shadow p-7 flex flex-col">
              {mtdTile.image ? (
                <img loading="lazy" decoding="async" src={mediaUrl(mtdTile.image, 700)} alt={mtdTile.alt || "MTD for Income Tax Checker"} className="w-full h-auto rounded-2xl border border-navy/5 mb-5" data-testid="assessment-card-image-mtd" />
              ) : (
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-orange/10 text-orange"><FileCheck2 size={26} /></div>
              )}
              <h2 className="font-display font-extrabold text-xl text-navy leading-snug">MTD for Income Tax Checker</h2>
              <p className="text-sm font-semibold text-slate-400 mt-1 mb-3">Are you affected — and from when?</p>
              <p className="text-sm text-slate-500 leading-relaxed flex-1">Making Tax Digital for Income Tax is coming for sole traders and landlords. Check in seconds whether it applies to you, when you'll be mandated, and what to do next.</p>
              <div className="flex items-center gap-2 text-xs text-slate-400 mt-5 mb-4"><Clock size={14} /> 20 seconds · instant result</div>
              <Link to="/making-tax-digital-income-tax" data-testid="assessment-cta-mtd" className="inline-flex items-center justify-center gap-2 w-full min-h-[3.25rem] text-center leading-tight bg-orange hover:bg-orange-dark text-white font-bold text-sm px-4 py-3 rounded-full transition-all hover:-translate-y-0.5">
                Check My MTD Status <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-3xl border border-navy/5 p-7 md:p-9 grid sm:grid-cols-3 gap-6">
            {[
              ["A personalised score", "See exactly where your business stands, out of 100."],
              ["The areas that need attention", "Your strongest and weakest areas, and the priority risks."],
              ["Clear next actions", "Practical recommendations and 90-day priorities to move forward."],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-3">
                <CheckCircle2 className="text-teal shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-display font-bold text-navy">{t}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{d}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Business Numbers Calculator banner */}
        </div>
      </section>

      {/* Free guides */}
      {guides.length > 0 && (
        <section className="py-14 md:py-20 bg-white" data-testid="assessment-guides">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-orange">Free downloads</span>
              <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-2">Guides & resources</h2>
              <p className="text-slate-500 max-w-2xl mt-3">Prefer to read at your own pace? Download our free, practical guides to help you run a healthier, more profitable and more independent business.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {guides.map((g, i) => (
                <div key={g.id} data-testid={`assessment-guide-${i}`} className="bg-slate-50 rounded-3xl border border-navy/5 overflow-hidden flex flex-col">
                  <div className="aspect-[16/9] bg-white flex items-center justify-center overflow-hidden border-b border-navy/5">
                    {g.image ? <img loading="lazy" decoding="async" src={mediaUrl(g.image, 600)} alt={g.image_alt || g.title} className="w-full h-full object-contain" /> : <FileText size={36} className="text-orange" />}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-bold text-lg text-navy leading-snug mb-2">{g.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-5 flex-1 line-clamp-3">{g.description}</p>
                    {g.file_url ? (
                      <button onClick={() => setActiveGuide(g)} data-testid={`assessment-guide-download-${i}`} className="inline-flex items-center gap-2 self-start bg-navy hover:bg-navy-deep text-white font-bold text-sm px-6 py-3 rounded-full transition-all hover:-translate-y-0.5">Free Download <Download size={15} /></button>
                    ) : (
                      <span className="inline-flex items-center gap-2 self-start text-slate-400 font-semibold text-sm">Coming soon</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <GuideDownloadModal guide={activeGuide} open={!!activeGuide} onClose={() => setActiveGuide(null)} />
    </div>
  );
}
